import React, { useState } from 'react';
import { IStaffAllocation } from '../../type/staffAllocation';
import { IOperationCheckList } from '../../type/operationCheckList';
import operationAPI from '../../api/operationApi';
import useUserStore from '../../store/userStore';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import useBgColorStore from '../../store/bgColor';

interface DriverDetailsProps {
  openModal?: any;
  updateDriverListOperations?: IStaffAllocation[];
  details: IOperationCheckList;
  isEditing: boolean;
  onEditEnd: () => void;
  onStaffChange: () => void;
  isAccordionClosed?: boolean;
}

const BookingDetailsCard: React.FC<DriverDetailsProps> = ({
  details,
  isEditing,
  onEditEnd,
  onStaffChange,
  isAccordionClosed,
}) => {
  const { bgColor, updateBgColor } = useBgColorStore();
  const user = useUserStore((state) => state.user);
  const [isChecked, setIsChecked] = useState(details?.actioned ?? false);

  const actionCheckListHandler = async (
    bookingProductId: number,
    operationsTriggerId: number,
    actioned: number,
  ) => {
    try {
      const response = await operationAPI.actionedOperationCheckList(
        bookingProductId,
        operationsTriggerId,
        actioned,
        user?.id,
      );
      toast.success(translate('operation.bookingActioned'));
      setTimeout(() => {
        onStaffChange();
      }, 1000);
    } catch (error) {
      toast.warn(translate('operation.commonError'));
      console.log(error);
    }
  };

  const handleCheckboxClick = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const checked = e.target.checked;
    setIsChecked(checked);
    const newValue = checked ? 1 : 0;
    try {
      await actionCheckListHandler(
        details?.bookingProductId,
        details?.operationsTriggerId,
        newValue,
      );
      onEditEnd();
    } catch (error) {
      console.error('Error handling action check list:', error);
      setIsChecked(!checked);
    }
  };

  return (
    <div className={`bg-backgroundColor-white border border-[#E0E6ED] rounded-md p-4 w-full`}>
      {/* Grid Container */}
      <div className="grid grid-cols-2 gap-4 text-start">
        {/* ID */}
        <div>
          <p className="text-sm text-text-lightGray font-normal">
            {translate('operationCheckList.id')}
          </p>
          <p className="text-sm font-normal text-text-darkGray">
            {details?.bookingId}
          </p>
        </div>

        {/* Type Title */}
        <div>
          <p className="text-sm text-text-lightGray font-normal">
            {translate('operationCheckList.title')}
          </p>
          <p className="text-sm font-normal text-text-darkGray">
            {details?.triggerDesc || 'N/A'}
          </p>
        </div>

        {/* Description */}
        <div className="col-span-2">
          <p className="text-sm text-text-lightGray font-normal">
            {translate('operationCheckList.description')}
          </p>
          <p className="text-sm font-normal text-text-darkGray">
            {details?.description || 'No Description'}
          </p>
        </div>

        {/* Tour */}
        <div>
          <p className="text-sm text-textColorGray font-normal">
            {translate('operationCheckList.tour')}
          </p>
          <p className="text-sm font-normal text-text-primary">
            {details?.productName || 'N/A'}
          </p>
        </div>

        {/* Agent */}
        <div>
          <p className="text-sm text-text-darkGray font-normal">
            {translate('operationCheckList.agent')}
          </p>
          <p className="text-sm font-normal text-text-primary">
            {details?.agentName || 'N/A'}
          </p>
        </div>

        {/* Pickup */}
        <div>
          <p className="text-sm text-textColorGray font-normal">
            {translate('operationCheckList.pickup')}
          </p>
          <p className="text-sm font-normal text-text-primary">
            {details?.pickup || 'N/A'}
          </p>
        </div>

        {/* Dropoff */}
        <div>
          <p className="text-sm text-textColorGray font-normal">
            {translate('operationCheckList.dropOff')}
          </p>
          <p className="text-sm font-normal text-text-primary">
            {details?.dropoff || 'N/A'}
          </p>
        </div>

        {/* Actioned by */}
        <div>
          <p className="text-sm text-textColorGray font-normal">
            {translate('operationCheckList.actionedBy')}
          </p>
          <p className="text-sm font-normal text-text-primary">
            {details?.staffName || 'N/A'}
          </p>
        </div>

        {/* Actioned Date */}
        <div>
          <p className="text-sm text-textColorGray font-normal">
            {translate('operationCheckList.actionedDate')}
          </p>
          <p className="text-sm font-normal text-text-primary">
            {details?.actionedDate || 'N/A'}
          </p>
        </div>

        {/* Adult, Child, Infant Counts */}
        <div className="col-span-2 flex justify-between gap-4">
          <div className={`flex-1 ${bgColor} rounded-lg p-4 text-center`}>
            <p className="text-sm text-textColorGray font-normal">
              {translate('operationCheckList.adult')}
            </p>
            <p className="text-sm font-normal text-text-primary">
              {details?.adults || 0}
            </p>
          </div>

          <div className={`flex-1 ${bgColor} rounded-lg p-4 text-center`}>
            <p className="text-sm text-textColorGray font-normal">
              {translate('operationCheckList.child')}
            </p>
            <p className="text-sm font-normal text-text-primary">
              {details?.children || 0}
            </p>
          </div>

          <div className={`flex-1 ${bgColor} rounded-lg p-4 text-center`}>
            <p className="text-sm text-textColorGray font-normal">
              {translate('operationCheckList.infant')}
            </p>
            <p className="text-sm font-normal text-text-primary">
              {details?.infants || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Actioned Checkbox */}
      <div className="flex items-center space-x-2 mt-4">
        <input
          type="checkbox"
          className="w-5 h-5 border-blue-500 rounded focus:ring-0 bg-backgroundColor-white checked:bg-blue checked:border-blue"
          checked={isChecked}
          disabled={!isEditing || isAccordionClosed}
          onChange={handleCheckboxClick}
        />
        <p className="text-sm text-textColor font-normal">
          {translate('operationCheckList.actioned')}
        </p>
      </div>
    </div>
  );
};

export default BookingDetailsCard;
